import { useLocalStorage } from "./useStorage";
import { useCallback, useEffect, useState } from "react";

function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    function callback() {
      setOnline(navigator.onLine);
    }
    window.addEventListener("online", callback);
    window.addEventListener("offline", callback);
    return () => {
      window.removeEventListener("online", callback);
      window.removeEventListener("offline", callback);
    };
  }, []);

  return online;
}

// the Network Information API is only really supported on chrome, therefore type info is also only partial
const connection: (NetworkInformation & { effectiveType?: "slow-2g" | "2g" | "3g" | "4g" }) | undefined = navigator.connection;
function useNetworkInformation() {
  const [info, setInfo] = useState(connection?.effectiveType);

  useEffect(() => {
    if (connection) {
      const callback = () => {
        setInfo(connection.effectiveType);
      };
      connection.addEventListener("change", callback);
      return () => connection.addEventListener("change", callback);
    }
  }, []);

  return info;
}

export function useNetworkState() {
  const [blocked, setBlocked] = useLocalStorage("piano_networkIsBlocked");

  const block = useCallback(() => setBlocked("true"), [setBlocked]);
  const unblock = useCallback(() => setBlocked(null), [setBlocked]);
  const toggleBlock = useCallback(() => setBlocked(blocked === "true" ? null : "true"), [blocked, setBlocked]);

  const online = useOnlineStatus();
  const network = useNetworkInformation();

  return {
    state: (blocked && online
      ? "online-blocked"
      : blocked && !online
      ? "offline-blocked"
      : online && (network === "slow-2g" || network === "2g")
      ? "online-slow"
      : !online
      ? "offline"
      : "online") as "online-blocked" | "offline-blocked" | "online-slow" | "offline" | "online",
    blocked: blocked === "true",
    block,
    unblock,
    toggleBlock,
  };
}
